import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { AlertBadge, RenderLocalesWithComponents, usePaginatedTable } from '@/_shared/components';
import { TableHeader } from '@/_shared/components/TableHeader';
import { getDocLink } from '@/_shared/constants';
import { useDialog } from '@/_shared/hooks/useDialog';
import {
  ImportedEmployer,
  getEmployeesImportCall,
  getEmployeesImportsOverviewCall,
  postEmployeesImportsTemplateCall,
} from '../../../state';
import { PAGE_SIZE_IMPORTED_EMPLOYEES } from '../../../state/constants';
import { FilterImportedProfiles } from './FilterImportedProfiles';
import { TableImportedProfiles } from './TableImportedProfiles';

export const EditImportedProfiles: FC = () => {
  const t = useTranslations('people.importCompanyData.verifyData.editImportedProfiles');
  const [selectedProfile, setSelectedProfile] = useState<ImportedEmployer | undefined>();
  const { jobId } = postEmployeesImportsTemplateCall.hook();
  const { pendingReviewImport } = getEmployeesImportsOverviewCall.hook();
  const { openDialog: openContacts } = useDialog('contacts');
  const {
    loading: loadingGetEmployeesImport,
    error: errorGetEmployeesImport,
    success: successGetEmployeesImport,
    execute: executeGetEmployeesImport,
    reset: resetGetEmployeesImport,
    totalItems,
    data,
  } = getEmployeesImportCall.hook();

  const [displayedData, setDisplayedData] = useState<ImportedEmployer[]>(data || []);

  const paginedProps = usePaginatedTable({
    rowData: displayedData,
    pageSize: PAGE_SIZE_IMPORTED_EMPLOYEES,
    totalItems,
  });

  const goToDocs = useCallback(() => {
    window.open(getDocLink('ADMIN_AND_DELEGATES_REVIEW_AND_CONFIRM'), '_blank');
  }, []);

  const goToContact = useCallback(() => {
    openContacts({});
  }, [openContacts]);

  useEffect(() => {
    if (
      !loadingGetEmployeesImport &&
      !successGetEmployeesImport &&
      !errorGetEmployeesImport &&
      (jobId || pendingReviewImport?.pendingJob)
    ) {
      executeGetEmployeesImport({
        jobId: jobId || (pendingReviewImport as { pendingJob: string }).pendingJob,
        start: 0,
        length: PAGE_SIZE_IMPORTED_EMPLOYEES,
      });
    }

    return () => {
      resetGetEmployeesImport();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, pendingReviewImport?.pendingJob]);

  useEffect(() => {
    if (successGetEmployeesImport) {
      setDisplayedData(data || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successGetEmployeesImport]);

  useEffect(() => {
    if (jobId) {
      executeGetEmployeesImport({
        jobId,
        start: 0,
        length: PAGE_SIZE_IMPORTED_EMPLOYEES,
      });
    }
  }, [executeGetEmployeesImport, jobId]);

  return (
    <Card maxWidth='100%' width='100%'>
      <TableHeader title={t('title')} filtersComponent={<FilterImportedProfiles />} />
      <Box mb='s' mt='xxs'>
        <Typography variant='p2/light' color='grey/light'>
          {t('subtitle')}
        </Typography>
      </Box>
      <TableImportedProfiles
        selectedProfile={selectedProfile}
        setSelectedProfile={setSelectedProfile}
        paginedProps={paginedProps}
        setDisplayedData={setDisplayedData}
        pageSize={PAGE_SIZE_IMPORTED_EMPLOYEES}
      />
      <AlertBadge
        containerProps={{ mt: 's' }}
        type='transparent'
        iconName='info'
        label={
          <Typography variant='p2/light'>
            <RenderLocalesWithComponents
              text={t('infoBox', {
                downloadFile: '!{DOCS}!',
              })}
              placeholders={{
                '!{DOCS}!': (
                  <Typography
                    as='span'
                    fontWeight='500'
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'inherit',
                    }}
                    onClick={goToDocs}
                  >
                    {t('labelDocs')}
                  </Typography>
                ),
                '!{CONTACT}!': (
                  <Typography
                    as='span'
                    fontWeight='500'
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'inherit',
                    }}
                    onClick={goToContact}
                  >
                    {t('labelContact')}
                  </Typography>
                ),
              }}
            />
          </Typography>
        }
      />
    </Card>
  );
};
